import {
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, DatePicker, Flex, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInstitutionServiceGetInstitutionEndpoint,
  useTreatmentServiceDeleteConsultationEndpoint,
  useTreatmentServiceGetConsultationListEndpoint,
  useTreatmentServiceGetConsultationListEndpointKey,
  useTreatmentServiceGetEpidemiologyCaseFormDraftEndpoint,
  useTreatmentServiceGetEpidemiologyCaseFormDraftEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ConsultationSortBy,
  ConsultationStatus,
  GetConsultationListResponseItem2,
  Permissions
} from 'src/api/requests'
import { Refresh2Icon } from 'src/assets'
import {
  DeleteConfirm,
  message,
  SearchBar,
  Select,
  TableActionButtons,
  Text
} from 'src/components'
import { usePatientBreadcrumb } from 'src/features/patients/hooks'
import {
  NewConsultationButton,
  ScheduleConsultationModal,
  useColumns,
  useConfig
} from 'src/features/persons/components'
import { useStyles } from 'src/features/persons/components/PersonConsultations/styles'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import {
  formErrorHandler,
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

export const PersonConsultations = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { id: personId } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const pagination = useTablePagination()
  const { options } = useConfig()
  const { columns } = useColumns()

  const { hasPermission } = useUser()
  const [isOpenFilters, setIsOpenFilters] = useState(false)
  const [search, setSearch] = useState('')
  const [date, setDate] = useState('')
  const [ordering, setOrdering] = useState('')

  const [isEpidemiologyClicked, setIsEpidemiologyClicked] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [consultationId, setConsultationId] = useState<string>()

  const isPatient = pathname.includes('patients')

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && { legalEntityName: search }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering) as ConsultationSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(!!date && { consultationDate: date }),
    personId
  }

  const { data, isLoading } =
    useTreatmentServiceGetConsultationListEndpoint(queryParams)

  const { data: epidemiologyCase, error: epidemiologyError } =
    useTreatmentServiceGetEpidemiologyCaseFormDraftEndpoint(
      { consultationId: String(consultationId) },
      [consultationId],
      { enabled: !!consultationId && isEpidemiologyClicked }
    )

  const { person } = usePatientBreadcrumb(personId)

  const { data: internalInstitution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      {
        id: String(person?.institution?.id)
      },
      [String(person?.institution?.id)],
      { enabled: !!person?.institution?.id }
    )

  const { data: institution } = useInstitutionServiceGetInstitutionEndpoint(
    {
      id: String(internalInstitution?.institutionId)
    },
    [String(internalInstitution?.institutionId)],
    { enabled: !!internalInstitution?.institutionId }
  )

  const { mutate: deleteMutation } =
    useTreatmentServiceDeleteConsultationEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('consultations.success_delete_consultation'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetConsultationListEndpointKey]
        })
      }
    })

  const getActionItems = useMemo(
    () =>
      (
        id: string,
        status: ConsultationStatus,
        canAddEpidemiologyCase: boolean,
        canEdit?: boolean
      ) => {
        const isPlanned = status === ConsultationStatus.SCHEDULED
        const isFinalized = status === ConsultationStatus.PRESENT

        const onEdit = () => {
          if (personId)
            navigate(
              isPatient
                ? paths.patients.consultations.edit(personId, id)
                : paths.persons.consultations.edit(personId, id)
            )
        }

        return [
          ...(isPlanned
            ? [
                {
                  key: 'reschedule',
                  label: t('consultations.reschedule_consultation'),
                  icon: <Refresh2Icon />,
                  onClick: () => {
                    setConsultationId(id)
                    setIsOpen(true)
                  }
                },
                {
                  key: 'edit',
                  label: t('consultations.manage_consultation'),
                  onClick: onEdit,
                  icon: <EditOutlined />
                }
              ]
            : []),
          // A performed consultation: only its author, on the day it was performed.
          ...(isFinalized && canEdit
            ? [
                {
                  key: 'edit-performed',
                  label: t('common.edit'),
                  onClick: onEdit,
                  icon: <EditOutlined />
                }
              ]
            : []),
          ...(isFinalized && isPatient && canAddEpidemiologyCase
            ? [
                {
                  key: 'epidemiology',
                  label: t('epidemiology.add_epidemiology_case'),
                  icon: <PlusOutlined />,
                  onClick: () => {
                    setIsEpidemiologyClicked(true)
                    setConsultationId(id)
                  }
                }
              ]
            : []),
          {
            key: 'details',
            label: t('common.details'),
            icon: <EyeOutlined />,
            onClick: () => {
              if (personId)
                navigate(
                  isPatient
                    ? paths.patients.consultations.details(personId, id)
                    : paths.persons.consultations.details(personId, id)
                )
            }
          },
          ...(isPlanned
            ? [
                {
                  key: 'delete',
                  label: (
                    <DeleteConfirm
                      title={t('consultations.delete_consultation')}
                      onConfirm={() => deleteMutation({ id })}
                    />
                  ),
                  danger: true
                }
              ]
            : [])
        ]
      },
    [t]
  )

  const tableColumns: ColumnType<GetConsultationListResponseItem2>[] = [
    ...columns,
    {
      key: 'action',
      render: ({ id, status, canAddEpidemiologyCase, canEdit }) => (
        <TableActionButtons
          justify="center"
          items={getActionItems(
            id,
            status,
            Boolean(canAddEpidemiologyCase) ||
              Boolean(institution?.declaresCases),
            canEdit
          )}
        />
      )
    }
  ]

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  const onToggleFilters = () => setIsOpenFilters((prev) => !prev)

  useEffect(() => {
    if (epidemiologyError && epidemiologyError instanceof ApiError) {
      formErrorHandler(epidemiologyError.body.errors)
      setIsEpidemiologyClicked(false)
      queryClient.removeQueries({
        queryKey: [useTreatmentServiceGetEpidemiologyCaseFormDraftEndpointKey]
      })
    }
  }, [epidemiologyError, epidemiologyCase])

  useEffect(() => {
    if (epidemiologyCase && isPatient) {
      navigate(
        paths.patients.epidemiologyCases.add(
          String(personId),
          String(consultationId)
        )
      )
    }
  }, [epidemiologyCase])

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" wrap="wrap">
        <Text className={styles.text}>{t('persons.person_consultations')}</Text>

        <Flex gap={8}>
          <SearchBar
            maxWidth={360}
            placeholder={t('consultations.search_placeholder')}
            onSearch={onSearch}
          />

          <Button icon={<SettingOutlined />} onClick={onToggleFilters}>
            {t('common.filters')}
          </Button>

          {hasPermission(
            Permissions.TREATMENT_CONSULTATION_CREATE_INSTITUTION
          ) && <NewConsultationButton />}
        </Flex>
      </Flex>

      {isOpenFilters && (
        <Flex justify="flex-end" gap={8}>
          <DatePicker
            format={dayMonthYearFormatWithDot}
            onChange={(val) =>
              setDate(val ? dayjs(val).format(dayMonthYearFormatWithDot) : '')
            }
          />

          <Select onSelect={onSelect} options={options} />
        </Flex>
      )}

      <Table
        rowKey="id"
        loading={isLoading}
        columns={tableColumns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {!!consultationId && isOpen && (
        <ScheduleConsultationModal
          onCancel={() => {
            setConsultationId(undefined)
            setIsOpen(false)
          }}
          consultationId={consultationId}
        />
      )}
    </Flex>
  )
}
